<template>
  <div class="courier-page">
    <div class="header-row">
      <div>
        <h2>配送员管理</h2>
        <p>管理配送员账号、负责区域、抢单权限和位置上报间隔。</p>
      </div>
      <el-button type="primary" @click="openCreate">新增配送员</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model="filters.keyword" placeholder="账号 / 姓名 / 手机号" clearable />
        <el-select v-model="filters.leader_id" placeholder="归属管理员" clearable filterable>
          <el-option v-for="leader in leaders" :key="leader.id" :label="leader.name" :value="leader.id" />
        </el-select>
        <el-select v-model="filters.province_id" placeholder="省份" clearable @change="handleFilterProvinceChange">
          <el-option v-for="item in provinces" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.city_id" placeholder="城市" clearable :disabled="!filters.province_id">
          <el-option v-for="item in filterCities" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.grab_mode" placeholder="抢单权限" clearable>
          <el-option label="只分单" value="assign_only" />
          <el-option label="只抢单" value="grab_only" />
          <el-option label="分单+抢单" value="assign_and_grab" />
        </el-select>
        <el-select v-model="filters.is_active" placeholder="启用状态" clearable>
          <el-option label="启用" :value="true" />
          <el-option label="停用" :value="false" />
        </el-select>
        <el-input-number v-model="filters.max_grab_distance_meters" :min="0" :max="100000" :step="100" controls-position="right" placeholder="接单距离" />
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card setting-card">
      <div class="setting-row">
        <div class="setting-label">配送员位置上报间隔（秒）</div>
        <div class="setting-control">
          <el-input-number v-model="locationSettings.upload_interval_seconds" :min="30" :max="86400" :step="30" />
          <el-button type="primary" :loading="savingSetting" @click="saveLocationSetting">保存</el-button>
        </div>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="couriers" stripe>
        <el-table-column prop="username" label="账号" min-width="130" />
        <el-table-column prop="name" label="姓名" min-width="110" />
        <el-table-column prop="phone" label="手机号" min-width="130" />
        <el-table-column label="负责区域" min-width="260">
          <template slot-scope="{ row }">
            <div class="region-tags">
              <el-tag v-for="item in row.courier_regions" :key="`${row.id}-${item.province_id}-${item.city_id}-${item.district_id || 0}-${item.street_id || 0}`" size="mini" effect="plain">
                {{ item.text }}
              </el-tag>
              <span v-if="!row.courier_regions || !row.courier_regions.length" class="muted-text">未配置</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="上报时间" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ formatDateTime(row.last_location_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="在线状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_online ? 'success' : 'info'" size="mini">{{ row.is_online ? "在线" : "离线" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上线时间" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ row.is_online ? formatDateTime(row.online_at) : "-" }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="leader_name" label="归属管理员" min-width="120">
          <template slot-scope="{ row }">{{ row.leader_name || "-" }}</template>
        </el-table-column>
        <el-table-column prop="grab_mode" label="抢单权限" min-width="120">
          <template slot-scope="{ row }">{{ grabModeText(row.grab_mode) }}</template>
        </el-table-column>
        <el-table-column label="接单距离" min-width="120">
          <template slot-scope="{ row }">{{ formatGrabDistance(row.max_grab_distance_meters) }}</template>
        </el-table-column>
        <el-table-column prop="created_by_name" label="创建人" min-width="120">
          <template slot-scope="{ row }">{{ row.created_by_name || "-" }}</template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="160">
          <template slot-scope="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="微信绑定" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.mini_bound ? 'success' : 'info'" size="mini">{{ row.mini_bound ? "已绑定" : "未绑定" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="审核状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="auditStatusTagType(row.courier_audit_status)" size="mini">{{ auditStatusText(row.courier_audit_status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="is_active" label="启用状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'info'" size="mini">{{ row.is_active ? "启用" : "停用" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="锁定状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="isLocked(row) ? 'danger' : 'success'" size="mini">{{ isLocked(row) ? "已锁定" : "正常" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="password_error_count" label="错误次数" width="90" />
        <el-table-column label="锁定截止" min-width="160">
          <template slot-scope="{ row }">{{ formatLockedUntil(row.locked_until) }}</template>
        </el-table-column>
        <el-table-column label="操作" min-width="380" fixed="right">
          <template slot-scope="{ row }">
            <div class="table-actions">
            <el-button size="mini" @click="openEdit(row)">编辑</el-button>
            <el-button v-if="canAudit(row)" size="mini" type="primary" plain @click="openAuditDialog(row)">审核</el-button>
            <el-button v-if="canUnlock(row)" size="mini" type="warning" plain @click="handleUnlock(row)">解锁账号</el-button>
            <el-button v-if="canResetPassword(row)" size="mini" type="warning" plain @click="openPasswordDialog(row)">修改密码</el-button>
            <el-button v-if="row.mini_bound" size="mini" type="danger" plain @click="unbindWechat(row)">解除微信绑定</el-button>
            <el-button size="mini" plain @click="openMap(row)">查看位置</el-button>
            <el-dropdown trigger="click" @command="(command) => handleMoreAction(command, row)">
              <el-button size="mini" plain type="info">
                更多<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="toggle_status">{{ row.is_active ? "停用" : "启用" }}</el-dropdown-item>
                <el-dropdown-item command="delete" class="danger-dropdown-item">删除</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="editingId ? '编辑配送员' : '新增配送员'"
      :visible.sync="showDialog"
      width="760px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="账号">
          <el-input v-model.trim="form.username" :disabled="!!editingId" />
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model.trim="form.phone" />
        </el-form-item>
        <el-form-item label="身份证号码">
          <el-input v-model.trim="form.id_card_no" />
        </el-form-item>
        <el-form-item v-if="!editingId" label="密码">
          <el-input v-model.trim="form.password" show-password placeholder="请输入登录密码" />
        </el-form-item>
        <el-form-item label="归属管理员">
          <el-select v-model="form.leader_id" clearable filterable style="width: 100%;" :disabled="currentUser.role === 'admin'">
            <el-option v-for="leader in leaders" :key="leader.id" :label="leader.name" :value="leader.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="抢单权限">
          <el-select v-model="form.grab_mode" style="width: 100%;">
            <el-option label="只分单" value="assign_only" />
            <el-option label="只抢单" value="grab_only" />
            <el-option label="分单+抢单" value="assign_and_grab" />
          </el-select>
        </el-form-item>
        <el-form-item label="接单距离">
          <div class="distance-control">
            <el-input-number v-model="form.max_grab_distance_meters" :min="0" :max="100000" :step="100" controls-position="right" class="distance-input" />
            <span class="distance-unit">M</span>
          </div>
        </el-form-item>
        <el-form-item label="负责区域">
          <div class="region-editor">
            <div v-for="(item, index) in form.courier_regions" :key="item.uid" class="region-row">
              <el-select v-model="item.province_id" clearable placeholder="省份" class="region-select" @change="handleRegionProvinceChange(index)">
                <el-option v-for="option in provinces" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.city_id" clearable placeholder="城市" class="region-select" :disabled="!item.province_id" @change="handleRegionCityChange(index)">
                <el-option v-for="option in item.cityOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.district_id" clearable placeholder="区县（选填）" class="region-select" :disabled="!item.city_id" @change="handleRegionDistrictChange(index)">
                <el-option v-for="option in item.districtOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.street_id" clearable placeholder="街道/乡镇（选填）" class="region-select" :disabled="!item.district_id">
                <el-option v-for="option in item.streetOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-button type="text" class="danger-text" :disabled="form.courier_regions.length === 1" @click="removeRegionRow(index)">删除</el-button>
            </div>
            <el-button size="mini" plain @click="addRegionRow">新增区域</el-button>
            <div class="form-tip">省份和城市必填，区县、街道/乡镇可选；不选下级表示负责所选上级区域。</div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm">提交</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="修改密码"
      :visible.sync="showPasswordDialog"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="passwordForm" label-width="100px">
        <el-form-item label="新密码">
          <el-input v-model.trim="passwordForm.password" show-password placeholder="请输入新密码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="submitPassword">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="配送员审核"
      :visible.sync="showAuditDialog"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="auditForm" label-width="100px">
        <el-form-item label="配送员">
          <span>{{ auditTarget ? `${auditTarget.name || auditTarget.username}（${auditTarget.phone || "-"}）` : "-" }}</span>
        </el-form-item>
        <el-form-item label="审核结果">
          <el-radio-group v-model="auditForm.action">
            <el-radio label="approve">通过</el-radio>
            <el-radio label="reject">拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="auditForm.remark" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showAuditDialog = false">取消</el-button>
        <el-button type="primary" @click="submitAudit">提交</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="配送员最近位置"
      :visible.sync="showMapDialog"
      width="960px"
      top="6vh"
      :close-on-click-modal="false"
    >
      <div v-if="selectedCourier" class="map-summary">
        <div class="map-summary-item">
          <span class="map-summary-label">姓名</span>
          <span>{{ selectedCourier.name || selectedCourier.username || "-" }}</span>
        </div>
        <div class="map-summary-item">
          <span class="map-summary-label">最近上报时间</span>
          <span>{{ formatDateTime(selectedCourier.last_location_at) }}</span>
        </div>
        <div class="map-summary-item">
          <span class="map-summary-label">坐标</span>
          <span>{{ formatLocation(selectedCourier) }}</span>
        </div>
      </div>
      <div v-if="mapUrl" class="map-box">
        <iframe :src="mapUrl" class="map-frame" frameborder="0"></iframe>
      </div>
      <div v-else class="empty-map">该配送员暂无最近位置</div>
    </el-dialog>
  </div>
</template>

<script>
import {
  createUser,
  deleteUser,
  auditCourier,
  fetchCities,
  fetchDistricts,
  fetchLeaders,
  fetchLocationSettings,
  fetchProvinces,
  fetchStreets,
  fetchUsers,
  unlockUser,
  unbindUserMiniWechat,
  updateLocationSettings,
  updateUser,
  updateUserPassword,
} from "../api/orders";
import { getStoredUser } from "../utils/auth";
import { getErrorMessage } from "../utils/errors";

const USERNAME_PATTERN = /^[A-Za-z0-9]+$/;

const createRegionRow = () => ({
  uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  province_id: null,
  city_id: null,
  district_id: null,
  street_id: null,
  cityOptions: [],
  districtOptions: [],
  streetOptions: [],
});

const emptyForm = () => ({
  username: "",
  name: "",
  phone: "",
  id_card_no: "",
  password: "",
  leader_id: null,
  grab_mode: "assign_only",
  courier_regions: [createRegionRow()],
});

export default {
  data() {
    return {
      currentUser: getStoredUser(),
      allowedRegionScopes: [],
      filters: {
        keyword: "",
        leader_id: "",
        province_id: "",
        city_id: "",
        grab_mode: "",
        is_active: "",
        max_grab_distance_meters: null,
      },
      couriers: [],
      leaders: [],
      provinces: [],
      filterCities: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDialog: false,
      editingId: null,
      form: emptyForm(),
      showPasswordDialog: false,
      passwordTargetId: null,
      passwordForm: { password: "" },
      showAuditDialog: false,
      auditTarget: null,
      auditForm: { action: "approve", remark: "" },
      locationSettings: { upload_interval_seconds: 300 },
      savingSetting: false,
      showMapDialog: false,
      mapUrl: "",
      selectedCourier: null,
    };
  },
  mounted() {
    this.loadBaseData();
  },
  methods: {
    grabModeText(mode) {
      if (mode === "grab_only") return "只抢单";
      if (mode === "assign_and_grab") return "分单+抢单";
      return "只分单";
    },
    auditStatusText(status) {
      const statusMap = {
        pending: "待审核",
        approved: "已通过",
        rejected: "已拒绝",
      };
      return statusMap[status || "approved"] || "已通过";
    },
    auditStatusTagType(status) {
      const typeMap = {
        pending: "warning",
        approved: "success",
        rejected: "danger",
      };
      return typeMap[status || "approved"] || "success";
    },
    canAudit(row) {
      return row && row.courier_audit_status !== "approved";
    },
    isLocked(row) {
      return !!(row && row.locked_until && new Date(row.locked_until).getTime() > Date.now());
    },
    formatLockedUntil(value) {
      if (!value || !this.isLocked({ locked_until: value })) return "-";
      return String(value).replace("T", " ").slice(0, 19);
    },
    formatDateTime(value) {
      if (!value) return "-";
      return String(value).replace("T", " ").slice(0, 19);
    },
    formatLocation(row) {
      if (row.last_latitude == null || row.last_longitude == null) return "-";
      return `${row.last_latitude}, ${row.last_longitude}`;
    },
    formatGrabDistance(value) {
      if (value === null || value === undefined || value === "") return "不限";
      const meters = Number(value);
      if (Number.isNaN(meters)) return "不限";
      if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km`;
      return `${meters}M`;
    },
    canUnlock(row) {
      return this.currentUser.role !== "sales" && (this.isLocked(row) || Number(row.password_error_count || 0) > 0);
    },
    canResetPassword(row) {
      if (!row || row.id === this.currentUser.id) return false;
      if (this.currentUser.role === "super_admin") return row.role !== "super_admin";
      return this.currentUser.role === "admin" && row.role === "courier";
    },
    async loadBaseData() {
      this.allowedRegionScopes = this.getCurrentUserRegionScopes();
      await Promise.all([this.loadProvinces(), this.loadLeaders(), this.loadLocationSettings()]);
      await this.loadCouriers();
    },
    getCurrentUserRegionScopes() {
      if (this.currentUser.courier_regions && this.currentUser.courier_regions.length) {
        return this.currentUser.courier_regions;
      }
      if (this.currentUser.province_id && this.currentUser.city_id) {
        return [{ province_id: this.currentUser.province_id, city_id: this.currentUser.city_id, district_id: null, street_id: null }];
      }
      return [];
    },
    isRegionWithinAdminScope(provinceId, cityId, districtId = null, streetId = null) {
      if (this.currentUser.role !== "admin") return true;
      return this.allowedRegionScopes.some((scope) => {
        if (scope.province_id !== provinceId || scope.city_id !== cityId) return false;
        if (scope.district_id && scope.district_id !== districtId) return false;
        if (scope.street_id && scope.street_id !== streetId) return false;
        return true;
      });
    },
    async loadLocationSettings() {
      try {
        const { data } = await fetchLocationSettings();
        this.locationSettings = data || { upload_interval_seconds: 300 };
      } catch (error) {
        this.locationSettings = { upload_interval_seconds: 300 };
      }
    },
    async saveLocationSetting() {
      this.savingSetting = true;
      try {
        await updateLocationSettings(this.locationSettings);
        this.$message.success("保存成功");
      } catch (error) {
        this.$message.error(getErrorMessage(error, "保存失败"));
      } finally {
        this.savingSetting = false;
      }
    },
    async loadProvinces() {
      const { data } = await fetchProvinces();
      if (this.currentUser.role === "admin" && this.allowedRegionScopes.length) {
        const ids = new Set(this.allowedRegionScopes.map((item) => item.province_id));
        this.provinces = (data || []).filter((item) => ids.has(item.id));
        return;
      }
      this.provinces = data || [];
    },
    async loadCities(parentId) {
      if (!parentId) return [];
      const { data } = await fetchCities(parentId);
      const cities = data || [];
      if (this.currentUser.role === "admin") {
        const ids = new Set(this.allowedRegionScopes.filter((item) => item.province_id === parentId).map((item) => item.city_id));
        return ids.size ? cities.filter((item) => ids.has(item.id)) : [];
      }
      return cities;
    },
    async loadDistricts(parentId) {
      if (!parentId) return [];
      const { data } = await fetchDistricts(parentId);
      const districts = data || [];
      if (this.currentUser.role === "admin") {
        const scopes = this.allowedRegionScopes.filter((item) => item.city_id === parentId);
        if (scopes.some((item) => !item.district_id)) return districts;
        const ids = new Set(scopes.map((item) => item.district_id));
        return districts.filter((item) => ids.has(item.id));
      }
      return districts;
    },
    async loadStreets(parentId) {
      if (!parentId) return [];
      const { data } = await fetchStreets(parentId);
      const streets = data || [];
      if (this.currentUser.role === "admin") {
        const scopes = this.allowedRegionScopes.filter((item) => item.district_id === parentId);
        if (scopes.some((item) => !item.street_id) || !scopes.length) return streets;
        const ids = new Set(scopes.map((item) => item.street_id));
        return streets.filter((item) => ids.has(item.id));
      }
      return streets;
    },
    async handleFilterProvinceChange() {
      this.filters.city_id = "";
      this.filterCities = await this.loadCities(this.filters.province_id);
    },
    async loadCouriers() {
      const params = {
        role: "courier",
        keyword: this.filters.keyword || undefined,
        leader_id: this.filters.leader_id || undefined,
        province_id: this.filters.province_id || undefined,
        city_id: this.filters.city_id || undefined,
        grab_mode: this.filters.grab_mode || undefined,
        is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
        max_grab_distance_meters: this.filters.max_grab_distance_meters === null ? undefined : this.filters.max_grab_distance_meters,
        page: this.pager.page,
        page_size: this.pager.page_size,
      };
      const { data } = await fetchUsers(params);
      this.couriers = data.items || [];
      this.total = data.total || 0;
    },
    async loadLeaders() {
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadCouriers();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadCouriers();
    },
    onSizeChange(size) {
      this.pager.page_size = size;
      this.pager.page = 1;
      this.loadCouriers();
    },
    addRegionRow() {
      this.form.courier_regions.push(createRegionRow());
    },
    removeRegionRow(index) {
      if (this.form.courier_regions.length === 1) return;
      this.form.courier_regions.splice(index, 1);
    },
    async handleRegionProvinceChange(index) {
      const item = this.form.courier_regions[index];
      item.city_id = null;
      item.district_id = null;
      item.street_id = null;
      item.cityOptions = await this.loadCities(item.province_id);
      item.districtOptions = [];
      item.streetOptions = [];
      this.$forceUpdate();
    },
    async handleRegionCityChange(index) {
      const item = this.form.courier_regions[index];
      item.district_id = null;
      item.street_id = null;
      item.districtOptions = await this.loadDistricts(item.city_id);
      item.streetOptions = [];
      this.$forceUpdate();
    },
    async handleRegionDistrictChange(index) {
      const item = this.form.courier_regions[index];
      item.street_id = null;
      item.streetOptions = await this.loadStreets(item.district_id);
      this.$forceUpdate();
    },
    async hydrateRegionRow(item) {
      item.cityOptions = await this.loadCities(item.province_id);
      if (item.city_id) {
        item.districtOptions = await this.loadDistricts(item.city_id);
      }
      if (item.district_id) {
        item.streetOptions = await this.loadStreets(item.district_id);
      }
    },
    async openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      if (this.currentUser.role === "admin") {
        if (!this.allowedRegionScopes.length) {
          this.$message.warning("当前管理员未配置负责区域，不能新增配送员");
          return;
        }
        const scope = this.allowedRegionScopes[0];
        this.form.leader_id = this.currentUser.id;
        this.form.courier_regions[0].province_id = scope.province_id;
        this.form.courier_regions[0].city_id = scope.city_id;
        this.form.courier_regions[0].district_id = scope.district_id || null;
        this.form.courier_regions[0].street_id = scope.street_id || null;
      }
      this.form.max_grab_distance_meters = null;
      for (const item of this.form.courier_regions) {
        await this.hydrateRegionRow(item);
      }
      this.showDialog = true;
    },
    async openEdit(row) {
      this.editingId = row.id;
      if (this.currentUser.role === "admin" && !this.allowedRegionScopes.length) {
        this.$message.warning("当前管理员未配置负责区域，不能编辑配送员");
        return;
      }
      this.form = {
        username: row.username,
        name: row.name,
        phone: row.phone,
        id_card_no: row.id_card_no || "",
        password: "",
        leader_id: row.leader_id || null,
        grab_mode: row.grab_mode || "assign_only",
        max_grab_distance_meters: row.max_grab_distance_meters ?? null,
        courier_regions: row.courier_regions && row.courier_regions.length
          ? row.courier_regions.map((item) => ({
              uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              province_id: item.province_id,
              city_id: item.city_id,
              district_id: item.district_id || null,
              street_id: item.street_id || null,
              cityOptions: [],
              districtOptions: [],
              streetOptions: [],
            }))
          : [createRegionRow()],
      };
      this.showDialog = true;
      for (const item of this.form.courier_regions) {
        await this.hydrateRegionRow(item);
      }
    },
    validateForm() {
      if (!this.form.phone) return this.$message.warning("请输入手机号");
      if (!this.form.id_card_no) return this.$message.warning("请输入身份证号码");
      if (!this.form.username) return this.$message.warning("请输入账号");
      if (!this.form.name) return this.$message.warning("请输入姓名");
      if (!USERNAME_PATTERN.test(this.form.username)) return this.$message.warning("账号只能使用英文字母和数字，英文字母区分大小写");
      if (!this.editingId && !this.form.password) return this.$message.warning("请输入密码");
      if (!this.form.leader_id) return this.$message.warning("请选择归属管理员");
      for (const item of this.form.courier_regions) {
        if (!item.province_id || !item.city_id) {
          return this.$message.warning("负责区域中省份和城市必填");
        }
        if (this.currentUser.role === "admin" && !this.isRegionWithinAdminScope(item.province_id, item.city_id, item.district_id, item.street_id)) {
          return this.$message.warning("配送员负责区域只能选择当前管理员自己的负责区域");
        }
      }
      return true;
    },
    async submitForm() {
      if (!this.validateForm()) return;
      const payload = {
        username: this.form.username,
        name: this.form.name,
        phone: this.form.phone,
        id_card_no: this.form.id_card_no,
        password: this.form.password || undefined,
        leader_id: this.form.leader_id,
        grab_mode: this.form.grab_mode,
        max_grab_distance_meters: this.form.max_grab_distance_meters || null,
        courier_regions: this.form.courier_regions.map((item) => ({
          province_id: item.province_id,
          city_id: item.city_id,
          district_id: item.district_id || null,
          street_id: item.street_id || null,
        })),
      };
      try {
        if (this.editingId) {
          await updateUser(this.editingId, payload);
          this.$message.success("更新成功");
        } else {
          await createUser({ ...payload, role: "courier" });
          this.$message.success("创建成功");
        }
        this.showDialog = false;
        this.loadCouriers();
      } catch (error) {
        this.$message.error(getErrorMessage(error, this.editingId ? "更新失败" : "创建失败"));
      }
    },
    async toggleStatus(row) {
      try {
        await updateUser(row.id, { is_active: !row.is_active });
        this.$message.success("状态已更新");
        this.loadCouriers();
      } catch (error) {
        this.$message.error(getErrorMessage(error, "状态更新失败"));
      }
    },
    async handleUnlock(row) {
      try {
        await this.$confirm(`确认解锁账号“${row.name || row.username}”吗？`, "提示", { type: "warning" });
        await unlockUser(row.id);
        this.$message.success("账号已解锁");
        this.loadCouriers();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, "解锁失败"));
        }
      }
    },
    openPasswordDialog(row) {
      this.passwordTargetId = row.id;
      this.passwordForm = { password: "" };
      this.showPasswordDialog = true;
    },
    async submitPassword() {
      if (!this.passwordForm.password) {
        this.$message.warning("请输入新密码");
        return;
      }
      try {
        await updateUserPassword(this.passwordTargetId, this.passwordForm.password);
        this.showPasswordDialog = false;
        this.$message.success("密码已更新");
      } catch (error) {
        this.$message.error(getErrorMessage(error, "密码修改失败"));
      }
    },
    openAuditDialog(row) {
      this.auditTarget = row;
      this.auditForm = { action: "approve", remark: "" };
      this.showAuditDialog = true;
    },
    async submitAudit() {
      if (!this.auditTarget) return;
      try {
        await auditCourier(this.auditTarget.id, this.auditForm);
        this.showAuditDialog = false;
        this.$message.success(this.auditForm.action === "approve" ? "审核已通过" : "审核已拒绝");
        this.loadCouriers();
      } catch (error) {
        this.$message.error(getErrorMessage(error, "审核失败"));
      }
    },
    handleMoreAction(command, row) {
      const actionMap = {
        toggle_status: () => this.toggleStatus(row),
        delete: () => this.removeCourier(row),
      };
      if (actionMap[command]) {
        actionMap[command]();
      }
    },
    async removeCourier(row) {
      try {
        await this.$confirm(`确认删除账号“${row.name || row.username}”吗？`, "提示", { type: "warning" });
        await deleteUser(row.id);
        this.$message.success("删除成功");
        this.loadCouriers();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, "删除失败"));
        }
      }
    },
    async unbindWechat(row) {
      try {
        await this.$confirm(`确认解绑“${row.name || row.username}”的微信吗？解绑后需要重新绑定才能登录小程序。`, "提示", { type: "warning" });
        await unbindUserMiniWechat(row.id);
        this.$message.success("微信已解绑");
        this.loadCouriers();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, "解绑失败"));
        }
      }
    },
    openMap(row) {
      this.selectedCourier = row;
      if (row.last_latitude == null || row.last_longitude == null) {
        this.mapUrl = "";
        this.showMapDialog = true;
        return;
      }
      const key = (this.locationSettings && this.locationSettings.tencent_map_key) || "";
      const title = encodeURIComponent(row.name || "配送员");
      this.mapUrl = `https://apis.map.qq.com/tools/poimarker?type=0&marker=coord:${row.last_latitude},${row.last_longitude};title:${title};addr:当前位置&key=${encodeURIComponent(key)}&referer=${encodeURIComponent("配送系统")}`;
      this.showMapDialog = true;
    },
  },
};
</script>

<style scoped>
.courier-page {
  min-height: 100%;
}
.setting-card {
  margin-bottom: 16px;
  padding: 16px 20px;
}
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.setting-label {
  font-size: 14px;
  color: #34495e;
}
.setting-control {
  display: flex;
  align-items: center;
  gap: 12px;
}
.distance-control {
  display: flex;
  align-items: center;
  gap: 8px;
}
.distance-input {
  width: 100%;
  flex: 1;
}
.distance-unit {
  flex: 0 0 auto;
  color: #8b9bb5;
  font-size: 13px;
}
.map-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
.map-summary-item {
  padding: 12px 14px;
  border: 1px solid #e8edf4;
  border-radius: 6px;
  background: #fafbfc;
  font-size: 13px;
  color: #34495e;
}
.map-summary-label {
  display: block;
  margin-bottom: 6px;
  color: #8b9bb5;
  font-size: 12px;
}
.map-box {
  width: 100%;
  height: 70vh;
}
.map-frame {
  width: 100%;
  height: 100%;
}
.empty-map {
  padding: 40px 0;
  text-align: center;
  color: #8b9bb5;
}
.danger-dropdown-item {
  color: #d93025;
}
.table-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.table-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}
</style>
